import {
    discoveryApiRef,
    fetchApiRef,
    useApi,
} from '@backstage/core-plugin-api';
import { useAsyncRetry } from 'react-use';

/**
 * The shape the catalog module answers with under /rtl/cmdb/issues.
 *
 * Repeated here rather than imported: the type lives in
 * `@internal/backstage-plugin-catalog-backend-module-rtl`, and a frontend
 * plugin that depends on a backend package pulls the whole backend into the
 * browser bundle. Kept in step by hand, as with every other HTTP contract of
 * this app.
 */
export type CmdbIngestionIssueKind =
    | 'nameless-entity'
    | 'duplicate-entity'
    | 'unresolved-team'
    | 'dangling-cluster-slice';

export type CmdbIngestionIssue = {
    kind: CmdbIngestionIssueKind;
    message: string;
    /** One entry per lost object, so the length of this is how many were lost. */
    refs: string[];
};

export type CmdbIngestionReport = {
    provider: string;
    exportLastModified: string;
    checkedAt: string;
    entityCount: number;
    issues: CmdbIngestionIssue[];
};

/**
 * What the current CMDB export loses on its way into the catalog.
 *
 * The backend maps the export afresh for this, so an answer can take a moment,
 * and it hands the same report out again for a minute - a retry inside that
 * window returns what was just seen rather than reading the dump again.
 */
export function useCmdbIngestionReport(): {
    report?: CmdbIngestionReport;
    loading: boolean;
    error?: Error;
    retry: () => void;
} {
    const discoveryApi = useApi(discoveryApiRef);
    const fetchApi = useApi(fetchApiRef);

    const { value, loading, error, retry } = useAsyncRetry(async () => {
        const baseUrl = await discoveryApi.getBaseUrl('catalog');
        const response = await fetchApi.fetch(`${baseUrl}/rtl/cmdb/issues`);

        if (!response.ok) {
            throw new Error(
                `Reading the CMDB ingestion issues failed with status ${response.status}`,
            );
        }

        return (await response.json()) as CmdbIngestionReport;
    }, [discoveryApi, fetchApi]);

    return { report: value, loading: loading, error: error, retry: retry };
}
